<template>
    <div v-if="!isLoading">
        <div class="variants" v-if="product" aria-label="Seleziona una variante">
            <div class="list-itemn">
                <div class="radiobutton" v-for="variant in product.variants">
                    <input type="radio" name="variantCode" :id="variant.code" :value="variant.code" v-model="variantCode" :disabled="isLoadingForm" @click="setVariant(variant)" />
                    <label :for="variant.code">{{ variant.title }}</label>
                </div>
            </div>
        </div>
        <div class="variants-desc">
            <div class="name" v-if="variant">{{ variant.description }}</div>
            <div class="editor">
                <div data-mrgt="20" class="wdg wdg-table mrg">
                    <table class="table" border="1" cellpadding="1" cellspacing="1" v-if="variant">
                        <thead>
                            <tr v-if="product.priceStepOption == '1'">
                                <th>{{ localizer.get("Catalogue.Common.UpTo25Units") }}</th>
                                <th>{{ localizer.get("Catalogue.Common.UpTo100Units") }}</th>
                                <th>{{ localizer.get("Catalogue.Common.Over100Units") }}</th>
                            </tr>
                            <tr v-else-if="product.priceStepOption == '2'">
                                <th>{{ localizer.get("Catalogue.Common.UpTo3Units") }}</th>
                                <th>{{ localizer.get("Catalogue.Common.UpTo10Units") }}</th>
                                <th>{{ localizer.get("Catalogue.Common.Over10Units") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="row-column-02" data-value="tue" data-start="true"><span class="unit">{{ localizer.get("Catalogue.Common.Euro") }}</span><span class="val">{{ formatPrice(variant.priceStep1) }}</span></td>
                                <td class="row-column-02" data-value="tue"><span class="unit">{{ localizer.get("Catalogue.Common.Euro") }}</span><span class="val">{{ formatPrice(variant.priceStep2) }}</span></td>
                                <td class="row-column-02" data-value="tue"><span class="unit">{{ localizer.get("Catalogue.Common.Euro") }}</span><span class="val">{{ formatPrice(variant.priceStep3) }}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-if="language === 'it'" class="disclaimer">{{ localizer.get("Catalogue.Common.PriceTaxDisclimer") }}</div>
        </div>
        <div class="qty">
            <div class="component" v-if="product">
                <div class="input">
                    <input name="BundleSize" type="number" class="textbox" v-model="quantity" :disabled="isLoadingForm" />
                    <div class="nav">
                        <button class="button fastic-angle-up" data-type="add" @click="addQuantity()"></button>
                        <button class="button fastic-angle-down" data-type="remove" @click="removeQuantity()"></button>
                    </div>
                </div>
            </div>
            <div class="note" v-if="product">{{ localizer.get("Catalogue.Common.PiecesInThePackage") }}: {{ product.quantity }}</div>
        </div>
        <div class="action mrg" data-mrgt="20">
            <div>
                <button v-if="!isLoadingForm" class="calltoaction" data-size="big" @click="submitForm()">
                    <span class="button fastic-cart">{{ localizer.get("Catalogue.Common.AddToCart") }}</span>
                </button>
                <button v-else class="calltoaction" data-size="big">
                    <span class="button fastic-cart">{{ localizer.get("Catalogue.Common.AddToCart") }}</span>
                </button>
                <a :href="routes.Support_InfoRequest + '?inforequest=moreinfo'" @click="openMoreInfo()" data-fancybox data-type="iframe" class="link">
                    <span class="icon fastic-info"></span><span class="label">{{ localizer.get("Catalogue.Common.WantMoreInformation") }}</span>
                </a>
            </div>
        </div>
        <div class="alert mrg" data-mrgt="20" v-for="(validationError, index) in validationErrors">{{ validationError.message }}</div>
        <div class="delivery mrg" data-mrgt="20"><span class="icon fastic-van"></span>{{ localizer.get("Catalogue.Common.FreeShippgingDisclimer") }} <span class="unit">{{ localizer.get("Catalogue.Common.Euro") }}</span> {{ freeShippingTotal }}</div>
        <div class="delivery-note mrg">{{ localizer.get("Catalogue.Common.FreeShippgingDisclimerNote") }}</div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from "vue";
import { useStore } from "vuex";
import Axios from "axios";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";
import Localizer from "@Scripts/Common/Localizer";
import FormHelper from "@Scripts/Common/FormHelper";

const store = useStore();

const props = defineProps<{
    productCode?: string,
    freeShippingTotal?: string,
    language?: string
}>();

const localizer = Localizer;
const routes = (window as any).App.routes;

const isLoading = ref(true);
const isLoadingForm = ref(false);
const validationErrors = ref<any[]>([]);
const variantCode = ref<string>(null);
const quantity = ref<number>(0);

const product = computed(() => {
    let productData = store.getters["catalogueStore/product"];
    quantity.value = productData ? productData.bundleSize : 1;
    return productData;
});

const variant = computed(() => {
    return store.getters["catalogueStore/variant"];
});

onBeforeMount(async () => {
    await getProductInfo();

    setAnalytics();
});

async function getProductInfo() {
    const response = (await Axios.post(routes.Catalogue_GetProductInfo, { code: props.productCode })).data;

    store.commit("catalogueStore/setProduct", response);

    setVariant(response.variants[0]);

    isLoading.value = false;
}

function formatPrice(data: string) {
    return Number(data).toFixed(2).replace(".", ",");
}

function setVariant(data: any) {
    store.commit("catalogueStore/setVariant", data);

    variantCode.value = data.code;
}

function addQuantity() {
    quantity.value = Number(quantity.value);

    if (quantity.value) {
        quantity.value += product.value.bundleSize;
    } else {
        quantity.value = product.value.bundleSize;
    }
    if (quantity.value > 1000) {
        quantity.value = 1000;
    }
}

function removeQuantity() {
    quantity.value = Number(quantity.value);

    if (quantity.value) {
        quantity.value -= product.value.bundleSize;
    } else {
        quantity.value = product.value.bundleSize;
    }
    if (quantity.value < product.value.bundleSize) {
        quantity.value = product.value.bundleSize;
    }
}

function openMoreInfo() {
    GTagHelper.sendEvent("click", "info_request_more_info", "open");
}

function setAnalytics() {
    GTagHelper.sendCustomEvent("view_item", {
        currency: "EUR",
        value: product.value.variants[0].priceStep1,
        items: [
            {
                item_id: product.value.code,
                item_name: product.value.title,
                affiliation: "Technical Ceramic",
                currency: "EUR",
                item_category: product.value.categoryName,
                price: product.value.variants[0].priceStep1,
                discount: 0
            }
        ]
    });
}

async function submitForm() {
    FormHelper.removeFocus();

    validationErrors.value = [];

    isLoadingForm.value = true;

    try {
        const response = (await Axios.post(routes.Cart_AddProduct, { productCode: product.value.code, variantCode: variant.value.code, quantity: quantity.value })).data;

        store.commit("cartStore/setCart", response.cartInfo);

        window.scrollTo({ top: 0, behavior: "smooth" });

        store.commit("cartStore/setIsCartPreviewVisible", true);

        setTimeout(function () {
            store.commit("cartStore/setIsCartPreviewVisible", false);
        }, 3000);

        var price = 0;

        if (quantity.value <= 25) {
            price = variant.value.priceStep1;
        } else if (quantity.value <= 100) {
            price = variant.value.priceStep2;
        } else {
            price = variant.value.priceStep3;
        }

        GTagHelper.sendCustomEvent("add_to_cart", {
            currency: "EUR",
            value: price * quantity.value,
            items: [
                {
                    item_id: product.value.code,
                    item_name: product.value.title,
                    affiliation: "Technical Ceramic",
                    currency: "EUR",
                    item_category: product.value.categoryName,
                    price: price,
                    discount: 0,
                    quantity: quantity.value
                }
            ]
        });
    } catch (error: any) {
        if (error.response && error.response.status === 400) {
            validationErrors.value = FormHelper.getValidationErrors(error.response.data);
        }
    }

    isLoadingForm.value = false;
}
</script>